import React, { useState, useEffect, useRef } from "react";
import ProfileOverview from "./profileOverview.js";
import LegalInfo from "./legalInfo.js";
import Toast from "../../components/Toast/toast.js";
import ErrorDisplay from "../../components/comman/ErrorDisplay.js";
import { useAuth } from "../../utils/authContext.js";
import LoadingSpinner from "../../components/LoadingSpinner/loadingSpinner.js";
import { useLanguage } from "../../utils/languageContext.js";
import { getTranslation } from "../../utils/translate.js";
import { updateChefStatus } from "../../services/profile-service.js";
import Toggle from "../../components/comman/toggle.js";

const Profile = () => {
    const [activeTab, setActiveTab] = useState("overview");
    const { chefData, refreshChefData, isLoading: isAuthLoading } = useAuth();
    const {language } = useLanguage();
    const [isToggling, setIsToggling] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const toastRef = useRef(null);
    const hasFetchedRef = useRef(false);

    // Fetch profile data on mount to trigger error handling (standardized pattern)
    useEffect(() => {
        // Wait for auth loading to complete
        if (isAuthLoading) return;
        
        // If already fetched, don't fetch again
        if (hasFetchedRef.current) return;
        
        const fetchProfileData = async () => {
            hasFetchedRef.current = true;
            setLoading(true);
            setError(null);
            
            try {
                // Refresh chef data to ensure we have latest data and catch any network errors
                await refreshChefData();
                setError(null);
            } catch (err) {
                console.error("Error fetching profile data:", err);
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        // Only fetch if we have chefData (meaning auth is successful)
        if (chefData) {
            fetchProfileData();
        }
    }, [isAuthLoading, chefData, refreshChefData]);

    const toggleActiveStatus = async () => {
        if (!chefData || isToggling) return;

        setIsToggling(true);
        const newStatus = chefData.status === "INACTIVE" ? "ACTIVE" : "INACTIVE";

        try {
            await updateChefStatus(chefData.id, newStatus);
            toastRef.current?.addToast(
                getTranslation('toast.statusUpdated', language) || "Status updated successfully",
                "success"
            );
            // Refresh chef data to get latest from server and update UI
            await refreshChefData();
        } catch (error) {
            console.error("Failed to update status:", error);
            toastRef.current?.addToast(
                getTranslation('toast.updateFailed', language) || "Status not updated",
                "error"
            );
        } finally {
            setIsToggling(false);
        }
    };

    // Show loading spinner while auth or profile data is being fetched
    if (isAuthLoading || loading) {
        return (
            <section className="pl-2">
                <LoadingSpinner />
            </section>
        );
    }

    // Show error display if there's an error (standardized pattern - same as other pages)
    if (error) {
        return (
            <section className="pl-2">
                <ErrorDisplay
                    error={error}
                    onRetry={async () => {
                        setError(null);
                        setLoading(true);
                        try {
                            await refreshChefData();
                            setError(null);
                        } catch (err) {
                            setError(err);
                        } finally {
                            setLoading(false);
                        }
                    }}
                />
            </section>
        );
    }

    // If no chefData and no error, still show loading (shouldn't happen but safety check)
    if (!chefData) {
        return (
            <section className="pl-2">
                <LoadingSpinner />
            </section>
        );
    }

    return (
        <section className="pl-2">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
                <h1 className="text-xl font-bold text-gray-900 dark:text-[#c9d1d9]">{getTranslation('menuItems.profile', language)}</h1>
                {/* Availability toggle */}
                {chefData && (
                    <div className="flex items-center gap-3 mt-2 sm:mt-0">
                        <Toggle
                            id="toggle"
                            checked={chefData.status === "ACTIVE"}
                            onChange={toggleActiveStatus}
                            disabled={isToggling}
                            toggleClassName={chefData.status === "ACTIVE" ? 'bg-green-500' : 'bg-red-500'}
                            className="flex items-center"
                            renderLabel={(toggleId) => (
                                <label htmlFor={toggleId} className={`text-sm font-medium flex items-center mr-3 ${chefData.status === "ACTIVE" ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                                    {chefData.status === "ACTIVE" 
                                        ? <span className="inline-flex items-center">
                                            <span className="w-2 h-2 bg-green-500 rounded-full mr-1.5 animate-pulse"></span>
                                            {getTranslation('menuItems.active', language)}
                                        </span> 
                                        : <span className="inline-flex items-center">
                                            <span className="w-2 h-2 bg-red-500 rounded-full mr-1.5"></span>
                                            {getTranslation('menuItems.inactive', language)}
                                        </span>
                                    }
                                </label>
                            )}
                        />
                        <div className="text-xs text-gray-500 dark:text-[#8b949e] font-medium hidden sm:block max-w-[200px]">
                            {getTranslation('menuItems.toggleStatusExplanation', language) || 'When inactive, you will not receive new orders'}
                        </div>
                    </div>
                )}
            </div>

            {/* Tab navigation - matching orders page style */}
            <div className="w-full max-w-md bg-white dark:bg-[#161b22] rounded-xl p-1.5 shadow-inner border dark:border-[#30363d] mb-5">
                <div className="relative flex">
                    <button
                        className={`relative flex-1 flex justify-center items-center px-4 py-2.5 rounded-lg text-sm font-semibold z-10 transition-all duration-200 ${
                            activeTab === "overview"
                                ? "text-gray-900 dark:text-[#c9d1d9]"
                                : "text-gray-700 dark:text-[#b1bac4] hover:text-gray-900 dark:hover:text-[#c9d1d9]"
                        }`}
                        onClick={() => setActiveTab("overview")}
                    >
                        <span>{getTranslation('profile.personal', language)}</span>
                    </button>
                    <button
                        className={`relative flex-1 flex justify-center items-center px-4 py-2.5 rounded-lg text-sm font-semibold z-10 transition-all duration-200 ${
                            activeTab === "legal"
                                ? "text-gray-900 dark:text-[#c9d1d9]"
                                : "text-gray-700 dark:text-[#b1bac4] hover:text-gray-900 dark:hover:text-[#c9d1d9]"
                        }`}
                        onClick={() => setActiveTab("legal")}
                    >
                        <span>{getTranslation('profile.legal', language)}</span>
                    </button>
                    
                    {/* Active tab indicator - sliding pill */}
                    <div 
                        className="absolute left-0 top-0 h-full w-1/2 bg-white dark:bg-[#1c2128] rounded-lg shadow-sm transform transition-transform duration-200 ease-in-out border dark:border-[#30363d]"
                        style={{
                            transform: activeTab === "legal" ? "translateX(100%)" : "translateX(0)"
                        }}
                    ></div>
                </div>
            </div>
            <div className="mt-4 transition-opacity duration-200">
                {/* Keep both components mounted to prevent image reloads when switching tabs */}
                <div style={{ display: activeTab === "overview" ? "block" : "none" }}>
                    <ProfileOverview chefData={chefData}/>
                </div>
                <div style={{ display: activeTab === "legal" ? "block" : "none" }}>
                    <LegalInfo chefData={chefData?.chefLegalDto}/>
                </div>
            </div>
            <Toast ref={toastRef} />
        </section>
    );
};
export default Profile;
